/**
 * Notebook data hooks (UX-reconciliation F4.1).
 *
 * The Editor's three-pane composition has a `<NotebooksTreePane>` (left pane) that
 * walks the notebooks tree level by level (matching the backend's
 * `NotebookRepo.list_children` pattern + `GET /api/v1/notebooks/children` endpoint).
 *
 * Each level fetches once and caches per parent — so expanding "Inbox" doesn't refetch
 * "Active Priorities". TanStack Query's `staleTime` is set generously (notebooks rarely
 * change at runtime) but refetch-on-mount stays on so a re-mount after a Joplin sync
 * surfaces new notebooks without a hard reload.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  createNotebook,
  deleteNotebook,
  fetchNotebookAncestors,
  fetchNotebookChildren,
  fetchNotesByNotebook,
  listNotebooks,
  renameNotebook,
  reparentNotebook,
  setNotebookAutorouter,
  updateNote,
} from '@/api';
import type {
  NotebookChildren,
  NotebookId,
  NotebookList,
  NotebookNoteList,
  NotebookSummary,
  NoteId,
} from '@/types';
import { useErrorHandler } from './useErrorHandler';

/** Stable query-key root — `['notebooks', 'children', parentId | 'root']`. */
const NOTEBOOK_CHILDREN_KEY = ['notebooks', 'children'] as const;
/** Stable query-key root for the notes-by-notebook lookups. */
const NOTEBOOK_NOTES_KEY = ['notebooks', 'notes'] as const;
/** Stable query-key root for the per-notebook ancestor chain lookups. */
const NOTEBOOK_ANCESTORS_KEY = ['notebooks', 'ancestors'] as const;

/**
 * `useNotebookChildrenQuery(parentId)` — the direct children of `parentId`, or the roots
 * when `parentId` is `null`.
 *
 * Keyed per-parent so each expanded subtree is independently cached. The hook is safe
 * to call with `parentId` toggled (changing the key skips the previous fetch).
 *
 * Returns the standard `useQuery` shape: `data` (a `NotebookChildren` envelope with
 * `parentId` echo + `notebooks` array), `isLoading`, `isError`, `refetch`.
 */
export function useNotebookChildrenQuery(
  parentId: NotebookId | null,
): UseQueryResult<NotebookChildren, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: [...NOTEBOOK_CHILDREN_KEY, parentId ?? '__root__'],
    queryFn: async () => {
      try {
        return await fetchNotebookChildren(parentId);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    staleTime: 60_000, // notebooks rarely change at runtime; revalidate after a minute.
  });
}

/**
 * `useNotebookNotesQuery(notebookId, enabled)` — paginated notes inside one notebook.
 *
 * Backs the editor's `<NotebooksTreePane>` inline note-list: when the user expands a
 * notebook the tree fetches both child notebooks AND the notes in that notebook,
 * rendering the notes as leaf rows underneath. The hook is `enabled`-gated so a
 * collapsed notebook does not pay for the fetch.
 *
 * Returns the standard `useQuery` shape with `data: NotebookNoteList`.
 */
export function useNotebookNotesQuery(
  notebookId: NotebookId | null,
  enabled: boolean,
): UseQueryResult<NotebookNoteList, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: [...NOTEBOOK_NOTES_KEY, notebookId ?? '__none__'],
    enabled: enabled && notebookId !== null,
    queryFn: async () => {
      if (notebookId === null) {
        throw new Error('useNotebookNotesQuery: notebookId is required');
      }
      try {
        return await fetchNotesByNotebook(notebookId);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

/**
 * Phase E.2 (2026-05-20) — mutation hook for user-authored notebook create.
 * Pass ``{ title, parentId }``. Invalidates the affected level (or roots
 * when parentId is null) so the tree refetches and the new notebook
 * appears.
 */
export function useCreateNotebookMutation(): UseMutationResult<
  { id: NotebookId; title: string; parentId: NotebookId | null; hasChildren: boolean; noteCount: number },
  Error,
  { title: string; parentId: NotebookId | null }
> {
  const { handleError } = useErrorHandler();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ title, parentId }) => {
      try {
        return await createNotebook(title, parentId);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    onSuccess: (_data, { parentId }) => {
      // Only the affected level needs a refetch — the new notebook lives
      // under `parentId` (or '__root__' for a top-level create).
      void qc.invalidateQueries({
        queryKey: [...NOTEBOOK_CHILDREN_KEY, parentId ?? '__root__'],
      });
      // If we created a subfolder, the parent's has_children flag may
      // need to flip true. Invalidate the GRANDPARENT level too so the
      // chevron toggle becomes visible.
      // Easy + over-cautious: invalidate the whole children root.
      void qc.invalidateQueries({ queryKey: NOTEBOOK_CHILDREN_KEY });
    },
  });
}

/**
 * Phase E.2 — mutation hook for notebook rename.
 */
export function useRenameNotebookMutation(): UseMutationResult<
  { id: NotebookId; title: string; parentId: NotebookId | null; hasChildren: boolean; noteCount: number },
  Error,
  { notebookId: NotebookId; title: string }
> {
  const { handleError } = useErrorHandler();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ notebookId, title }) => {
      try {
        return await renameNotebook(notebookId, title);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    onSuccess: () => {
      // The title can appear on any level; refresh all children queries
      // and ancestor lookups.
      void qc.invalidateQueries({ queryKey: NOTEBOOK_CHILDREN_KEY });
      void qc.invalidateQueries({ queryKey: NOTEBOOK_ANCESTORS_KEY });
    },
  });
}

/**
 * Phase E.2 — mutation hook for notebook soft-delete.
 * Notes inside are NOT cascaded — they retain their notebook_id for
 * recovery via the Trash folder pinned at the bottom of the notebook tree.
 */
export function useDeleteNotebookMutation(): UseMutationResult<
  { accepted: boolean; message: string },
  Error,
  NotebookId
> {
  const { handleError } = useErrorHandler();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (notebookId) => {
      try {
        return await deleteNotebook(notebookId);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTEBOOK_CHILDREN_KEY });
      void qc.invalidateQueries({ queryKey: NOTEBOOK_NOTES_KEY });
      void qc.invalidateQueries({ queryKey: NOTEBOOK_ANCESTORS_KEY });
    },
  });
}

/**
 * Drag-and-drop reparent — move a notebook under a new parent (`null` = top level).
 * Invalidates every children level + ancestor chain + the flat list so the tree reshapes.
 * The backend rejects cycles + the protected Inbox; those surface via the shared error handler.
 */
export function useReparentNotebookMutation(): UseMutationResult<
  { id: NotebookId; title: string; parentId: NotebookId | null; hasChildren: boolean; noteCount: number },
  Error,
  { notebookId: NotebookId; parentId: NotebookId | null }
> {
  const { handleError } = useErrorHandler();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ notebookId, parentId }) => {
      try {
        return await reparentNotebook(notebookId, parentId);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTEBOOK_CHILDREN_KEY });
      void qc.invalidateQueries({ queryKey: NOTEBOOK_ANCESTORS_KEY });
      void qc.invalidateQueries({ queryKey: ['list_notebooks'] });
    },
  });
}

/**
 * Drag-and-drop move a note into a different notebook (via `update_note`'s notebookId).
 * Invalidates the notes-by-notebook lists + flat note-titles so the source and destination
 * folders both refresh.
 */
export function useMoveNoteMutation(): UseMutationResult<
  unknown,
  Error,
  { noteId: NoteId; notebookId: NotebookId }
> {
  const { handleError } = useErrorHandler();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ noteId, notebookId }) => {
      try {
        return await updateNote({ id: noteId, notebookId });
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTEBOOK_NOTES_KEY });
      void qc.invalidateQueries({ queryKey: ['note-titles'] });
      void qc.invalidateQueries({ queryKey: NOTEBOOK_CHILDREN_KEY });
    },
  });
}

/** The protected system Archive — a root-level notebook with this exact title (mirrors the
 *  backend's ARCHIVE_NOTEBOOK_NAME contract). */
const ARCHIVE_NOTEBOOK_TITLE = 'Archive';

/**
 * Archive a folder (2026-06-12): re-parent it under the root-level system "Archive"
 * notebook, creating that notebook on first use. The Archive folder is protected on the
 * backend (no rename/move/delete) and renders in its own bottom section of the tree, like
 * Trash. On creation, autorouting is switched OFF for the Archive (best-effort) — the AI
 * should never file new notes into archived structure; the ancestor check then covers
 * everything inside it.
 */
export function useArchiveNotebookMutation(): UseMutationResult<
  unknown,
  Error,
  { notebookId: NotebookId }
> {
  const { handleError } = useErrorHandler();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ notebookId }) => {
      try {
        // Resolve (or create) the root-level Archive. Fetch fresh rather than trusting the
        // cache — a stale miss would create a duplicate "Archive".
        const roots = await fetchNotebookChildren(null);
        let archive = roots.notebooks.find(
          (nb) => nb.parentId === null && nb.title === ARCHIVE_NOTEBOOK_TITLE,
        );
        if (archive === undefined) {
          const created = await createNotebook(ARCHIVE_NOTEBOOK_TITLE, null);
          archive = { ...created, autorouterEnabled: true };
          // Best-effort: the router must not file into archives. Failure is non-fatal —
          // the user can flip the folder dot manually.
          try {
            await setNotebookAutorouter(created.id, false);
          } catch {
            /* non-fatal */
          }
        }
        if (archive.id === notebookId) {
          throw new Error('The Archive folder cannot be archived into itself.');
        }
        return await reparentNotebook(notebookId, archive.id);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTEBOOK_CHILDREN_KEY });
      void qc.invalidateQueries({ queryKey: NOTEBOOK_ANCESTORS_KEY });
      void qc.invalidateQueries({ queryKey: ['list_notebooks'] });
    },
  });
}

/**
 * `useNotebookAncestorsQuery(notebookId)` — root-down chain of a notebook,
 * inclusive of the input id. Backs `<NotebooksTreePane>` auto-expand: when
 * the user arrives at a note in a nested folder, every ancestor in the
 * returned chain gets expanded so the note's row is visible.
 *
 * `null` notebookId is a no-op (returns empty data); used when the active
 * note has no notebookId or there's no active note. Cached aggressively
 * (notebooks rarely re-parent).
 */
export function useNotebookAncestorsQuery(
  notebookId: NotebookId | null,
): UseQueryResult<{ notebookIds: NotebookId[] }, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: [...NOTEBOOK_ANCESTORS_KEY, notebookId ?? '__none__'],
    enabled: notebookId !== null,
    queryFn: async () => {
      if (notebookId === null) {
        throw new Error('useNotebookAncestorsQuery: notebookId is required');
      }
      try {
        return await fetchNotebookAncestors(notebookId);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    staleTime: 5 * 60_000, // notebook hierarchy rarely changes
  });
}

/**
 * `useListNotebooksQuery()` — the flat list of every live notebook (id + title + parentId).
 *
 * Cached under `['list_notebooks']` (the same key the change-event stream + notebook mutations
 * invalidate), so it stays fresh after renames/moves. Backs the editor breadcrumb's
 * notebook-id→title→path resolution (no more raw notebook UUIDs in the UI).
 */
export function useListNotebooksQuery(): UseQueryResult<NotebookList, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: ['list_notebooks'],
    queryFn: async () => {
      try {
        const { data } = await listNotebooks({});
        return data;
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

/** Depth bound mirroring the backend's notebook-tree walk guard (adapters.py `_MAX_NOTEBOOK_DEPTH`). */
const MAX_NOTEBOOK_DEPTH = 64;

/**
 * Resolve a notebook id to its root→leaf **title path** (e.g. `['2026', 'June']`) by walking the
 * `parentId` chain over the flat notebook list. Pure + cycle-guarded + depth-bounded. Returns an
 * empty array when `notebookId` is null or the id isn't found (e.g. the list hasn't loaded yet) —
 * callers render a graceful fallback rather than a raw UUID.
 */
export function resolveNotebookPath(
  notebookId: NotebookId | null,
  notebooks: NotebookSummary[],
): string[] {
  if (notebookId === null) return [];
  const byId = new Map(notebooks.map((n) => [n.notebookId, n]));
  const path: string[] = [];
  const seen = new Set<NotebookId>();
  let current: NotebookId | null = notebookId;
  for (let i = 0; i < MAX_NOTEBOOK_DEPTH && current !== null; i++) {
    if (seen.has(current)) break; // cyclic tree guard
    seen.add(current);
    const nb = byId.get(current);
    if (nb === undefined) break; // unknown id (list not loaded / cross-workspace)
    path.push(nb.title);
    current = nb.parentId;
  }
  return path.reverse(); // root → leaf
}
